// src/start/model.ts: the Start Your Build form's pure logic (docs/onsite/ONSITE.md 3). Everything tenant-specific
// comes from the public tenant: the form (steps, options, reveals, paths), the rooms, the ideas' images and the
// prefill projection (what each answer adds to the board). No DOM, no network: unit-tested in tests/app.
import type { StartAnswers } from "../../shared/api";
import type { FormStep, PublicFormPath, PublicFunnelForm, PublicTenant } from "../../shared/tenant";
import { titleCase } from "../lib/format";
import { roomCover } from "../lib/room-cover";

/** The contact step, the last step of every path (it is not in tenant.funnel.form.steps). */
export const CONTACT = "contact";

/** What the visitor has answered. `location` and the contact fields never reach browser storage. */
export type Answers = {
  interest?: string;
  land?: string;
  community?: string;
  location?: string;
  timing?: string;
  household?: string;
  musts?: string[];
  /** Must-haves pre-checked by an earlier answer ("From your last answer"). */
  implied?: string[];
  budget?: string;
  remodelType?: string;
  remodelScope?: string;
};
export type AnswerKey = keyof Answers;

// ------------------------------------------------------------------ paths and steps

export function pathFor(form: PublicFunnelForm, interest: string | undefined): PublicFormPath | undefined {
  return interest ? form.paths.find((p) => p.interest === interest) : undefined;
}

/** The step ids this visitor walks: the path's steps, then contact. Before the first answer, just the first step. */
export function stepIds(form: PublicFunnelForm, interest: string | undefined): string[] {
  const p = pathFor(form, interest);
  if (!p) return [form.steps[0]?.id ?? "interest"];
  return [...p.steps, CONTACT];
}

export function stepById(form: PublicFunnelForm, id: string): FormStep | undefined {
  return form.steps.find((s) => s.id === id);
}

/** Live paths get a board now; a held path (a remodel) goes to the team. Before the first answer: live. */
export function isLivePath(form: PublicFunnelForm, interest: string | undefined): boolean {
  const p = pathFor(form, interest);
  return !p || p.outcome === "live";
}

/** The reveal shown under a step for the current answer ("Where is it?" under "I have my own lot"). */
export function revealFor(step: FormStep, answer: string | undefined) {
  if (!answer) return undefined;
  return (step.reveal ?? []).find((r) => r.when.includes(answer));
}

/** The answer a step stores (musts is a list). */
function answerOf(a: Answers, step: FormStep): string | string[] | undefined {
  if (step.field === "musts") return a.musts;
  return a[step.field as Exclude<AnswerKey, "musts" | "implied">] as string | undefined;
}

/** Whether a step has what it needs to move on. Must-haves are optional; a required reveal must be answered. */
export function stepDone(form: PublicFunnelForm, a: Answers, id: string): boolean {
  if (id === CONTACT) return false;
  const step = stepById(form, id);
  if (!step) return false;
  if (step.multi) return true;
  const v = answerOf(a, step);
  if (typeof v !== "string" || !v) return false;
  const r = revealFor(step, v);
  if (r && !r.optional && r.field === "community") return !!a.community;
  return true;
}

/** The step to show for a requested one: it must be on the path, with every step before it done. */
export function validStep(form: PublicFunnelForm, a: Answers, requested: string | null, first: string): string {
  const ids = stepIds(form, a.interest);
  const start = Math.max(0, ids.indexOf(first));
  const want = requested && ids.includes(requested) ? requested : first;
  const target = ids.indexOf(want);
  for (let i = start; i < target; i++) {
    const id = ids[i]!;
    if (!stepDone(form, a, id)) return id;
  }
  return target >= start ? want : ids[start]!;
}

export function nextStep(form: PublicFunnelForm, a: Answers, id: string): string | null {
  const ids = stepIds(form, a.interest);
  const i = ids.indexOf(id);
  return i >= 0 && i < ids.length - 1 ? ids[i + 1]! : null;
}

export function prevStep(form: PublicFunnelForm, a: Answers, id: string): string | null {
  const ids = stepIds(form, a.interest);
  const i = ids.indexOf(id);
  return i > 0 ? ids[i - 1]! : null;
}

/** Seconds a person spends on each kind of step (the progress line's "About N min left"). */
const SECONDS: Record<string, number> = {
  interest: 8, land: 20, community: 12, timing: 10, household: 10, musts: 20, budget: 10, remodelType: 10, remodelScope: 10, [CONTACT]: 30,
};

/**
 * "About 2 minutes" on the first step, "About N min left", "Last step"; and how far along the bar is. The bar eases
 * out, moving fast on the first answers and slowly near the end: in randomized survey experiments a bar that is fast
 * early cuts drop-off and a steady one does not (Villar, Callegaro & Yang 2013, 32 experiments).
 */
export function progress(form: PublicFunnelForm, a: Answers, id: string, first: string): { label: string; pct: number } {
  const ids = stepIds(form, a.interest ?? form.paths[0]?.interest);
  const i = Math.max(0, ids.indexOf(id));
  const pct = Math.round(8 + 92 * (1 - (1 - i / ids.length) ** 2));
  if (id === CONTACT) return { label: "Last step", pct };
  if (id === first && first === (form.steps[0]?.id ?? "interest")) return { label: "About 2 minutes", pct };
  const secs = ids.slice(i).reduce((s, sid) => s + (SECONDS[sid === CONTACT ? CONTACT : (stepById(form, sid)?.field ?? "")] ?? 10), 0);
  return { label: `About ${Math.max(1, Math.round(secs / 60))} min left`, pct };
}

/**
 * Changing "What brings you here?" drops the answers the new path never asks (ONSITE 3.1). The server recomputes
 * the path anyway. A held path drops the home; just exploring keeps it.
 */
export function dropOffPath(form: PublicFunnelForm, a: Answers): Answers {
  const p = pathFor(form, a.interest);
  if (!p) return { ...(a.interest ? { interest: a.interest } : {}) };
  const asked = new Set<string>(["interest"]);
  for (const id of p.steps) {
    const s = stepById(form, id);
    if (!s) continue;
    asked.add(s.field);
    for (const r of s.reveal ?? []) asked.add(r.field);
  }
  const out: Answers = {};
  for (const [k, v] of Object.entries(a) as [AnswerKey, unknown][]) {
    const field = k === "implied" ? "musts" : k;
    if (asked.has(field)) (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/** The answers the server takes (StartRequest.answers). */
export function toStartAnswers(a: Answers): StartAnswers {
  const out: StartAnswers = { interest: a.interest ?? "" };
  for (const k of ["land", "community", "location", "timing", "household", "budget", "remodelType", "remodelScope"] as const) {
    const v = a[k];
    if (typeof v === "string" && v.trim()) out[k] = v.trim();
  }
  if (a.musts?.length) out.musts = [...a.musts];
  return out;
}

// ------------------------------------------------------------------ the board the answers set up (tenant rooms + prefill)

export type PreviewRoom = {
  id: string;
  name: string;
  img: string | null;
  flags: string[];
  /** Ideas from the home a `?home=` link named, as short labels. */
  from: string[];
  /** A room this visitor's answers added (In-law suite, Kids' rooms). */
  added: boolean;
};

/** The room's cover in the preview: the first of its starter ideas that has an image, else the room's cover. */
function roomImage(t: PublicTenant, ideaIds: readonly string[], roomKey: string): string | null {
  for (const id of ideaIds) {
    const idea = t.ideas.find((i) => i.id === id);
    if (idea?.image) return idea.image.srcSmall || idea.image.src;
  }
  return roomCover(t, roomKey);
}

type Entry = { addRooms?: string[]; flagRooms?: { room: string; flag: string }[] };

function prefillEntries(t: PublicTenant, a: Answers, form: PublicFunnelForm): Entry[] {
  const p = t.startPrefill;
  if (!p) return [];
  const out: Entry[] = [];
  const own = (rec: Record<string, Entry> | undefined, key: string | undefined) => {
    if (rec && key && Object.prototype.hasOwnProperty.call(rec, key)) out.push(rec[key]!);
  };
  const path = pathFor(form, a.interest);
  const asks = (field: string) => !path || path.steps.some((id) => {
    const s = stepById(form, id);
    return s?.field === field || (s?.reveal ?? []).some((r) => r.field === field);
  });
  if (asks("musts")) for (const m of a.musts ?? []) own(p.features, m);
  if (asks("land")) own(p.land, a.land);
  if (asks("community")) own(p.community, a.community);
  if (asks("household")) own(p.household, a.household);
  return out;
}

/** The rooms of the board these answers make, in board order, with the flags each answer sets. */
export function boardPreview(t: PublicTenant, a: Answers, home: string | null): PreviewRoom[] {
  const form = t.form;
  const rooms: PreviewRoom[] = [...t.rooms]
    .sort((x, y) => x.order - y.order)
    .map((r) => ({ id: r.id, name: titleCase(r.name), img: roomImage(t, r.ideas, r.id), flags: [], from: [], added: false }));
  if (!form) return rooms;
  const entries = prefillEntries(t, a, form);
  for (const e of entries) {
    for (const key of e.addRooms ?? []) {
      if (rooms.some((r) => r.id === key)) continue;
      const s = t.roomSuggestions.find((x) => x.id === key) ?? t.rooms.find((x) => x.id === key);
      if (s) rooms.push({ id: s.id, name: titleCase(s.name), img: roomImage(t, s.ideas, s.id), flags: [], from: [], added: true });
    }
  }
  for (const e of entries) {
    for (const f of e.flagRooms ?? []) {
      const r = rooms.find((x) => x.id === f.room);
      if (r && !r.flags.includes(f.flag)) r.flags.push(f.flag);
    }
  }
  if (home) {
    for (const idea of t.ideas) {
      if (!idea.builtIn.some((b) => b.homeId === home)) continue;
      const r = rooms.find((x) => x.id === idea.room);
      if (r) r.from.push(idea.label);
    }
  }
  return rooms;
}

export function isNoted(r: PreviewRoom): boolean {
  return r.flags.length > 0 || r.from.length > 0 || r.added;
}

/** What the last answer did to the board, for the phone bar: "Walkout · Lower Level", "In-Law Suite". */
export function noteFor(t: PublicTenant, field: string, value: string): string | null {
  const p = t.startPrefill;
  if (!p) return null;
  const rec = field === "musts" ? p.features : field === "land" ? p.land : field === "community" ? p.community : field === "household" ? p.household : null;
  const e = rec && Object.prototype.hasOwnProperty.call(rec, value) ? rec[value] : undefined;
  if (!e) return null;
  const roomName = (id: string) => {
    const r = t.rooms.find((x) => x.id === id) ?? t.roomSuggestions.find((x) => x.id === id);
    return r ? titleCase(r.name) : null;
  };
  const f = e.flagRooms?.[0];
  if (f) {
    const n = roomName(f.room);
    return n ? `${f.flag} · ${n}` : f.flag;
  }
  const added = e.addRooms?.[0];
  return added ? roomName(added) : null;
}

/** The label of an option value on the step that asks `field`. */
export function optionLabel(form: PublicFunnelForm, field: string, value: string | undefined): string {
  if (!value) return "";
  for (const s of form.steps) {
    if (s.field !== field) continue;
    const o = s.options.find((x) => x.value === value);
    if (o) return o.label;
  }
  return "";
}

/** The facts on the desktop panel, from the answers so far (enum answers only). */
export function railFacts(form: PublicFunnelForm, a: Answers): [string, string][] {
  const out: [string, string][] = [];
  const interest = a.interest;
  if (interest) {
    const label = optionLabel(form, "interest", interest);
    const p = pathFor(form, interest);
    if (p?.outcome === "held") out.push(["Project", optionLabel(form, "remodelType", a.remodelType) || label]);
    else if (p?.looking) out.push(["Looking", label]);
    else out.push(["Building", label]);
  }
  if (a.community) out.push(["Community", optionLabel(form, "community", a.community)]);
  if (a.timing) out.push(["Timing", optionLabel(form, "timing", a.timing)]);
  if (a.household) out.push(["Household", optionLabel(form, "household", a.household)]);
  if (a.budget) out.push(["Budget", optionLabel(form, "budget", a.budget)]);
  return out.filter(([, v]) => !!v);
}

// ------------------------------------------------------------------ contact

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export function looksLikeEmail(s: string): boolean {
  return EMAIL.test(s.trim());
}

/** Common typos of the big inbox domains (ONSITE 13 "Did you mean ...?"). */
const TYPO_DOMAINS: Record<string, string> = {
  "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gnail.com": "gmail.com", "gmail.co": "gmail.com", "gmal.com": "gmail.com",
  "gamil.com": "gmail.com", "gmaill.com": "gmail.com", "hotmial.com": "hotmail.com", "hotmai.com": "hotmail.com",
  "yaho.com": "yahoo.com", "yahooo.com": "yahoo.com", "yahoo.co": "yahoo.com", "outlok.com": "outlook.com", "iclod.com": "icloud.com",
  "icoud.com": "icloud.com", "aol.co": "aol.com",
};
export function emailSuggestion(email: string): string | null {
  const e = email.trim().toLowerCase();
  const at = e.lastIndexOf("@");
  if (at < 1) return null;
  const fix = TYPO_DOMAINS[e.slice(at + 1)];
  return fix ? `${e.slice(0, at)}@${fix}` : null;
}

/** An address becomes its first letter, three dots and the domain ("j•••" then the "@domain"). */
export function maskEmail(email: string): string {
  const e = email.trim();
  const at = e.lastIndexOf("@");
  if (at < 1) return e;
  return `${Array.from(e)[0]}•••${e.slice(at)}`;
}

/** The first word of a name, for "Thank you, Jen." (letters, apostrophes and hyphens only, else ""). */
export function firstWord(name: string): string {
  const w = name.trim().split(/\s+/)[0] ?? "";
  return /^[\p{L}][\p{L}'-]{0,29}$/u.test(w) ? w : "";
}

// ------------------------------------------------------------------ ids

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/** A ULID for StartRequest.submissionId: made when the tab starts the form; an idempotency key only. */
export function newSubmissionId(now = Date.now(), rand: (n: number) => Uint8Array = randomBytes): string {
  let time = "";
  let t = now;
  for (let i = 0; i < 10; i++) {
    time = CROCKFORD[t % 32]! + time;
    t = Math.floor(t / 32);
  }
  const bytes = rand(16);
  let r = "";
  for (let i = 0; i < 16; i++) r += CROCKFORD[bytes[i]! % 32]!;
  return time + r;
}

function randomBytes(n: number): Uint8Array {
  const b = new Uint8Array(n);
  crypto.getRandomValues(b);
  return b;
}

/** A random id for this tab's funnel events (never keyed to a person). */
export function newVisitId(): string {
  return newSubmissionId().slice(-20).toLowerCase();
}
